import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useAuth } from "@/contexts/AuthContext"
import { createQuiz, getExtensionQuestionList, getFreestyleQuestionListV3, getKnowledgePointList, getRecapQuestionList, getSchoolPress, getTopicList, getTopicListByQuestionInfoIdList, publishAssignment, registerClass, useGetAssignmentListByStudent, useGetKnowledgePointList, useGetTopicList } from "@/services/generatedAPIs"
import { useSubjectList } from "@/hooks/use-subject-list"
import { CascadingSelect, type CascadingOption } from "@/components/cascading-select"
import React, { useCallback, useEffect, useState, useTransition } from "react"
import { useTranslation, WithTranslation, withTranslation } from "react-i18next"
import { useSWRConfig } from "swr"
import { cn } from "@/lib/utils"
import { FilePlus, FileSearch2, FileX } from "lucide-react"
import { NavigateFunction, useNavigate } from "react-router"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { MultiSelect } from "./multi-select"
import { QuestionInfo, QuestionLabel, QuestionModel, UserInfoResponse } from "@/services/generatedAPIs.schemas"
import { ROUTE } from "@/routes/constants"
import styles from './css/CreatePracticeDialog.module.css';
import AddIcon from '@/assets/images/add.svg';
import StarIcon from '@/assets/images/star.svg';
import QuestionIcon from '@/assets/images/question.svg';
import { LoadingOverlay } from "@/components/ui/loading-overlay"
import { Press } from "@/utils/constants"
import { isEN } from "@/utils/i18n"
import { toast } from "sonner"
import { Slider } from "@/components/ui/slider"

export enum PracticeType {
  ExtendPractice,
  RecapPractice,
  FreePractice
}

/* 隨心練弹窗统一样式（按 Figma）：
   强调色 = 默认主题品牌绿，其余主题跟随主色（见 global.css --practice-dialog-accent）。
   - ACCENT_BTN：保存按钮（绿底 / 大圆角 / 居中）
   - SLIDER_ACCENT：滑块已填充段与滑钮描边跟随强调色（仅作用于本弹窗内的 Slider 实例） */
const DIALOG_ACCENT_STYLE: React.CSSProperties = {
  backgroundColor: 'var(--practice-dialog-accent)',
  color: 'var(--practice-dialog-accent-fg)',
};
const ACCENT_BTN = 'h-12 rounded-2xl px-12 text-base font-bold shadow-sm transition hover:brightness-95';
const SLIDER_ACCENT =
  '[&_[data-slot=slider-range]]:bg-[var(--practice-dialog-accent)] [&_[data-slot=slider-thumb]]:border-[var(--practice-dialog-accent)]';

/* 自由主题下拉（知識點 / 題庫）Figma 配色覆盖 —— 仅默认（orange）主题：
   - 高亮（hover / 鍵盤聚焦）背景 = #E6E7EB 浅灰
   - 勾选框选中色 = #9CEA4F 品牌绿（与 --practice-dialog-accent 同源）
   通过祖先选择器 `html[data-theme=orange]` 收紧到默认主题；
   green/blue/pink 三套主题继续走原 `--accent` / `--primary`，本覆盖不生效。
   Popover 走 portal 挂在 body 上，但 html 永远是它祖先，所以祖先选择器可达。 */
const DIALOG_POPOVER_ACCENT = [
  '[html[data-theme=orange]_&_[data-slot=command-item][data-selected=true]]:!bg-[#E6E7EB]',
  '[html[data-theme=orange]_&_[data-slot=command-item][data-selected=true]]:!text-foreground',
  '[html[data-theme=orange]_&_.bg-primary]:!bg-[#9CEA4F]',
  '[html[data-theme=orange]_&_.border-primary]:!border-[#9CEA4F]',
].join(' ');

const Card = ({ title, description, className, icon }) => {
  return (
    <div className={cn('h-full flex flex-col items-center justify-between rounded-xl p-6 shadow-md', className)}>
        <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
        <p className="text-white mb-8 min-h-[48px]">{description}</p>
        <div className={styles.icon}>
          {icon}
        </div>
    </div>
  );
};


const onSubmit = async (subjectId: number, subject: string, questionModelList: QuestionModel[], currentUser: UserInfoResponse, navigate: NavigateFunction, incremental?: boolean, language?: string, titleKey?: string) => {
  try {
    // setIsLoading(true);
    const questionInfoIds = questionModelList.map((questionModel) => questionModel.questionInfo.id!);
    const topicIdsRsp = await getTopicListByQuestionInfoIdList({ questionInfoIdList: questionInfoIds });
    const topicIds = topicIdsRsp.data!.topicInfos.map((questionLabel) => { return questionLabel.id! });
    const quizRsp = await createQuiz({
      title: "",
      subject: subject,
      subjectId: subjectId,
      originName: currentUser.name,
      originType: "student",
      language: language ?? "zh-HK",
      teacher_id: currentUser.id,
      topicIds: topicIds,
      questionInfoIds: questionInfoIds,
    })
    const assignmentRsp = await publishAssignment({
      title: "",
      type: "practice",
      studentId: currentUser.id,
      quizId: quizRsp.data!.id,
      topicIds: topicIds,
    })
    console.log('publish assignment ', assignmentRsp);
    if (assignmentRsp.code === 200 && assignmentRsp.data) {
      // titleKey: 入口模块的标题翻译键 (更多習題/錯題重練/自由主題) → PracticeEditPage 顶部标题改成入口名
      navigate(ROUTE.STUDENT.PRACTICE_EDIT, { state: { assignmentId: assignmentRsp.data, studentId: currentUser.id, incremental: incremental, titleKey: titleKey } });
    }
  } catch (e) {
    console.log('error publish assignment ', e);
  } finally {
    // setIsLoading(false);
  }
};

type RenderTriggerArg = { onClick: () => void; isLoading?: boolean };
type RenderTriggerProp = { renderTrigger?: (arg: RenderTriggerArg) => React.ReactNode };

export function ExtendPracticeDialog({ renderTrigger }: RenderTriggerProp) {
  const { getCurrentUser } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { subjectInfos, isLoading: subjectLoading } = useSubjectList();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isLoading = subjectLoading || isSubmitting;
  const firstSubject = subjectInfos[0];

  const handleExtend = () => {
    if (isLoading) return;
    if (!firstSubject) { toast.info(t('common.subjectLoading')); return; }
    setIsSubmitting(true);
    getExtensionQuestionList({ studentId: getCurrentUser()!.id, assignmentId: 0 }).then((rsp) => {
      console.log('getExtensionQuestionList', rsp.data?.questionModelList);
      if (rsp.code === 200 && rsp.data) {
        if (!rsp.data.questionModelList?.length) { toast.info(t('component.noQuestionText') ?? 'No questions available.'); setIsSubmitting(false); return; }
        onSubmit(firstSubject.id!, firstSubject.name!, rsp.data.questionModelList, getCurrentUser()!, navigate, true, i18n.language, 'component.exttitle');
      }
    }).catch((e) => {
      console.log('getExtensionQuestionList error', e)
    }).finally(() => {
      setIsSubmitting(false);
    });
  };

  return (
    <>
      <LoadingOverlay isLoading={isLoading} text={t('common.loading')} />
      {renderTrigger ? (
        renderTrigger({ onClick: handleExtend, isLoading })
      ) : (
        <div onClick={handleExtend}>
          <Card
            title={t('component.exttitle')}
            description={t('component.extdesc')}
            className="bg-yellow-400 transition-transform transform hover:scale-105 duration-300"
            icon={<img src={AddIcon} width="60px" height="60px"></img>}
          />
        </div>
      )}
    </>
    )
}

export function RecapPracticeDialog({ renderTrigger }: RenderTriggerProp) {
  const { getCurrentUser } = useAuth();
  const [value, setValue] = useState<number>(7);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [emptyResult, setEmptyResult] = useState(false);
  const { subjectInfos, isLoading: subjectLoading } = useSubjectList();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isLoading = subjectLoading || isSubmitting;
  const firstSubject = subjectInfos[0];

  return (
    <>
      <LoadingOverlay isLoading={isLoading} text={t('common.loading')} />
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setEmptyResult(false); }}>
      <DialogTrigger asChild>
        <div className="flex w-full md:min-h-0 md:flex-1">
          {renderTrigger ? (
            renderTrigger({ onClick: () => setOpen(true), isLoading })
          ) : (
            <Card
              title={t('component.recaptitle')}
              description={t('component.recapdesc')}
              className="bg-red-400 transition-transform transform hover:scale-105 duration-300"
              icon={<img src={QuestionIcon} width="60px" height="60px"></img>}
            />
          )}
        </div>
      </DialogTrigger>
      <DialogContent showTitle={false} className="flex flex-col gap-8 rounded-3xl p-8 sm:p-10 sm:max-w-md" onOpenAutoFocus={e=>e.preventDefault()}>
        <DialogTitle className="text-3xl font-bold text-foreground">{t('component.selecttime')}</DialogTitle>
        {emptyResult ? (
          <div className="flex flex-col items-center gap-6 py-4">
            <p className="text-center text-gray-600">
              {t('component.recapEmpty')}
            </p>
            <Button className={ACCENT_BTN} style={DIALOG_ACCENT_STYLE} onClick={() => {
              setOpen(false);
              navigate(ROUTE.STUDENT.PRACTICE_HOME);
            }}>
              {t('component.backToPractice')}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex flex-row items-center justify-center gap-4 text-nowrap">
              <Label htmlFor="lastDayCount" className="text-base">{t('component.recaplast')}</Label>
              <Input name="lastDayCount" className="invalid:border-red-500 h-12 w-24 rounded-2xl bg-secondary/50 text-center text-lg" type="text" onInvalid={(e: any) => { e.target.setCustomValidity(t('component.error')); }} onInput={(e: any) => { setValue(e.target.value); e.target.setCustomValidity(""); }} defaultValue={value} required />
              <Label htmlFor="lastDayCount" className="text-base">{t('component.recapday')}</Label>
            </div>
            <DialogFooter className="sm:justify-center">
              <Button type="submit" className={ACCENT_BTN} style={DIALOG_ACCENT_STYLE} onClick={(e) => {
                if (isNaN(value) || value <= 0) {
                  toast(t('component.error'));
                  e.preventDefault();
                  return;
                }

                if (isLoading) return;
                setIsSubmitting(true);
                getRecapQuestionList({ studentId: getCurrentUser()!.id, lastDayCount: value }).then((rsp) => {
                  console.log('getRecapQuestionList', value, rsp.data?.questionModelList);
                  if (rsp.code === 200 && rsp.data) {
                    const questions = rsp.data.questionModelList;
                    if (!questions || questions.length === 0) {
                      setEmptyResult(true);
                      toast.info(t('component.noQuestionText') ?? 'No questions available.');
                      setIsSubmitting(false);
                      return;
                    }
                    setOpen(false);
                    if (!firstSubject) { toast.info(t('common.subjectLoading')); return; }
                    onSubmit(firstSubject.id!, firstSubject.name!, questions, getCurrentUser()!, navigate, undefined, i18n.language, 'component.recaptitle');
                  }
                }).catch((e) => {
                  console.log('getRecapQuestionList error', e)
                }).finally(() => {
                  setIsSubmitting(false);
                });
              }}>{t('component.confirm')}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
    </>
  )
}

export function FreePracticeDialog({ renderTrigger }: RenderTriggerProp) {
  const { getCurrentUser } = useAuth();
  const navigate = useNavigate();
  const { subjectInfos, isLoading: subjectLoading } = useSubjectList();
  const firstSubject = subjectInfos[0];
  const [topicInfos, setTopicInfos] = useState<QuestionLabel[]>([]);
  const [knowledgePointInfos, setKnowledgePointInfos] = useState<QuestionLabel[]>([]);

  const [pressList, setPressList] = React.useState<string[]>([]);
  const [libraryTypes, setLibraryTypes] = React.useState<string[]>([]);

  const [selectedKnowledgePointIds, setSelectedKnowledgePointIds] = React.useState<string[]>([])
  const [difficultyRange, setDifficultyRange] = React.useState<number[]>([0, 100])
  const [questionCount, setQuestionCount] = React.useState<number>(5)
  const { t, i18n } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isLoading = subjectLoading || isSubmitting;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    getTopicList({ teacherId: getCurrentUser()!.id, schoolName: getCurrentUser()!.schoolName }).then((rsp) => {
      if (rsp.code === 200 && rsp.data) {
        setTopicInfos(rsp.data.topicInfos);
      }
    }).catch((e) => {
    });

    getKnowledgePointList({ teacherId: getCurrentUser()!.id, schoolName: getCurrentUser()!.schoolName, topicId: '' }).then((rsp) => {
      if (rsp.code === 200 && rsp.data) {
        setKnowledgePointInfos(rsp.data.knowledgePoints);
      }
    }).catch((e) => {
    });
  }, []);

  useEffect(() => {
    getSchoolPress({
      schoolName: getCurrentUser()!.schoolName,
    }).then((rsp) => {
      if (rsp.code === 200 && rsp.data) {
        setPressList(rsp.data);
      } else {
        console.log('getSchoolPress empty ');
      }
    }).catch((err) => {
      console.log('getSchoolPress error ', err);
    });
  }, [])

  // 构建级联选择器的选项数据
  const cascadingOptions = React.useMemo((): CascadingOption[] => {
    if (!topicInfos || !knowledgePointInfos) {
      return [];
    }

    return topicInfos.map(topic => ({
      label: isEN(i18n) ? (topic.enName || topic.name!) : topic.name!,
      value: topic.id!.toString(),
      children: knowledgePointInfos
        .filter(kp => kp.mtrId && kp.mtrId.startsWith(topic.mtrId + '-'))
        .map(kp => ({
          label: isEN(i18n) ? (kp.enName || kp.name!) : kp.name!,
          value: kp.id!.toString(),
        }))
    })).filter(option => option.children && option.children.length > 0);
  }, [topicInfos, knowledgePointInfos, i18n]);



  return (
    <>
      <LoadingOverlay isLoading={isLoading} text={t('common.loading')} />
      <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div className="flex w-full md:min-h-0 md:flex-1">
          {renderTrigger ? (
            renderTrigger({ onClick: () => setOpen(true), isLoading })
          ) : (
            <Card
              title={t('component.freetitle')}
              description={t('component.freedesc')}
              className="bg-teal-400 transition-transform transform hover:scale-105 duration-300"
              icon={<img src={StarIcon} width="60px" height="60px"></img>}
            />
          )}
        </div>
      </DialogTrigger>
      <DialogContent showTitle={false} className="flex flex-col gap-7 rounded-3xl p-8 sm:p-10 sm:max-w-2xl" onOpenAutoFocus={e=>e.preventDefault()}>
        <DialogTitle className="text-3xl font-bold text-foreground">{t('component.selecttopic')}</DialogTitle>

        {/* 知識點 + 題庫：md+ 两列并排（Figma） */}
        <div className="grid gap-6 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <Label className="text-base font-semibold">{t('component.selectknowledgepoint')}</Label>
          <CascadingSelect
            options={cascadingOptions}
            value={selectedKnowledgePointIds}
            onChange={setSelectedKnowledgePointIds}
            placeholder={t('component.selectknowledgepoint')}
            className="w-full"
            popoverClassName={DIALOG_POPOVER_ACCENT}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="library" className="text-base font-semibold">
            {t("teacher.selectlibrary")}
          </Label>
          <MultiSelect
            className="col-span-3"
            popoverClassName={DIALOG_POPOVER_ACCENT}
            options={[
              ...pressList.map((press) => { 
                for (const p of Press) {
                  if (p.nameEn === press) {
                    return { label: isEN(i18n) ? press : p.name, value: press } 
                  }
                }
                throw new Error('Press not found');
              }),
              { label: t('teacher.myschool'), value: getCurrentUser()!.schoolName },
              { label: 'CE/DSE', value: 'CE/DSE' },
              { label: 'TSA', value: 'TSA' },
              { label: t('teacher.aiquestion'), value: 'AI' },
              { label: t('teacher.others'), value: 'OTHERS' },
            ].map((option) => { return { value: option.value, label: option.label } })}
            onValueChange={setLibraryTypes}
            defaultValue={libraryTypes}
            placeholder=""
            variant="default"
          />
        </div>
        </div>

        {/* Difficulty Selection */}
        <div className="flex flex-col gap-2">
          <Label className="text-base font-semibold">{t('component.difficulty')}</Label>
          <div className='flex flex-col w-full justify-center items-center'>
            <div className="text-sm font-medium text-foreground mb-2">
              {difficultyRange[0]} - {difficultyRange[1]}
            </div>
            <Slider
              defaultValue={difficultyRange}
              min={0}
              max={100}
              step={1}
              className={cn("px-2 w-full", SLIDER_ACCENT)}
              onValueChange={(value) => {
                setDifficultyRange(value);
              }}
            />
            <div className='flex flex-row place-content-between w-full mt-2'>
              <span className="text-xs text-gray-500">{t("common.easy")}</span>
              <span className="text-xs text-gray-500">{t("common.median")}</span>
              <span className="text-xs text-gray-500">{t("common.hard")}</span>
            </div>
          </div>
        </div>

        {/* Question Count Slider */}
        <div className="flex flex-col gap-2">
          <Label className="text-base font-semibold">{t('component.questioncount')}</Label>
          <div className="flex flex-row gap-4 items-center">
            <Slider
              defaultValue={[questionCount]}
              min={1}
              max={10}
              step={1}
              className={cn("flex-1", SLIDER_ACCENT)}
              onValueChange={(value) => {
                setQuestionCount(value[0]);
              }}
            />
            <span className="text-nowrap min-w-[3rem] text-right">{questionCount}{t("common.q")}</span>
          </div>
        </div>

        <DialogFooter className="sm:justify-center">
          <DialogClose asChild>
            <Button type="submit" className={ACCENT_BTN} style={DIALOG_ACCENT_STYLE} onClick={(e) => {
              // Validate parameters before making the request
              if (!selectedKnowledgePointIds || selectedKnowledgePointIds.length === 0) {
                toast.error(t('component.selectknowledgepoint'));
                e.preventDefault();
                return;
              }

              if (!libraryTypes || libraryTypes.length === 0) {
                toast.error(t('teacher.selectlibrary'));
                e.preventDefault();
                return;
              }

              if (!questionCount || questionCount < 1 || questionCount > 10) {
                toast.error(t('component.error'));
                e.preventDefault();
                return;
              }

              // Use difficulty range from slider
              const minDiff = difficultyRange[0];
              const maxDiff = difficultyRange[1];

              if (minDiff < 0 || maxDiff > 100 || minDiff > maxDiff) {
                toast.error(t('component.error'));
                e.preventDefault();
                return;
              }

              if (isLoading) return;
              setIsSubmitting(true);
              getFreestyleQuestionListV3({
                studentId: getCurrentUser()!.id,
                topicIds: [],
                knowledgePointIds: selectedKnowledgePointIds.map((value) => parseInt(value)),
                libraryNames: libraryTypes,
                questionCount: questionCount,
                minDifficulty: minDiff,
                maxDifficulty: maxDiff
              }).then((rsp) => {
                console.log('getFreestyleQuestionListV3', selectedKnowledgePointIds, questionCount, difficultyRange, rsp.data?.questionModelList);
                if (rsp.code === 200 && rsp.data) {
                  if (!rsp.data.questionModelList?.length) { toast.info(t('component.noQuestionText') ?? 'No questions available.'); setIsSubmitting(false); return; }
                  if (!firstSubject) { toast.info(t('common.subjectLoading')); setIsSubmitting(false); return; }
                  onSubmit(firstSubject.id!, firstSubject.name!, rsp.data.questionModelList, getCurrentUser()!, navigate, undefined, i18n.language, 'component.freetitle');
                }
              }).catch((e) => {
                console.log('getFreestyleQuestionListV3 error', e)
              }).finally(() => {
                setIsSubmitting(false);
              });
            }}>{t('component.confirm')}</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}
